import { useEffect, useRef, useState } from "react";

/**
 * How long the figure takes to travel, in milliseconds.
 *
 * The marketing site's, to the frame: its pricing section tweens the same two
 * numbers over 0.6s when the same switch is thrown, and somebody who has just
 * come from that page to this one is looking at the same offer. A different
 * speed here would read as a different control.
 */
const DURATION_MS = 600;

/**
 * GSAP's `power2.out`, which is the ease the site tweens with — cubic, and all
 * of its speed at the start. The figure leaves the old price immediately and
 * settles onto the new one, rather than drifting off and slamming in.
 */
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Whether the figure should simply appear at its new value.
 *
 * The reader's own `prefers-reduced-motion`, read at the moment a tween would
 * start rather than subscribed to: the answer only matters when a number is
 * about to move, and a change of mind mid-tween is 600ms from being over.
 *
 * An environment with no `matchMedia` to ask — jsdom, under test — counts as a
 * yes. Somewhere that cannot report the preference is not somewhere painting
 * sixty frames a second either, and a test that has to wait out an animation to
 * read a price is a test that will one day fail for having waited 40ms too few.
 */
const skipAnimation = () =>
  typeof window === "undefined" ||
  typeof window.matchMedia !== "function" ||
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A number that counts to its new value instead of jumping to it.
 *
 * For the price on the trial screen, where a switch above the figure swaps one
 * cycle's price for another's. Snapping reads as the card being replaced; the
 * count reads as the same price being re-worked, which is what actually
 * happened — one product, one of two terms.
 *
 * `null` means there is no figure yet, and it travels straight through: the
 * first real value is *seeded*, not counted up to from nothing. A price rising
 * from ₹0 as the list lands would be an animation of a number that was never on
 * screen, and the screen would open on a lie about what the plan costs.
 *
 * The tween carries on from wherever it had got to when the target changed —
 * the same object GSAP would be mutating — so throwing the switch twice in a
 * second sends the figure on from where it stands rather than snapping it back
 * to the price it started from.
 *
 * Returns a whole number: these are rupees, and the fractions are only how it
 * gets between two of them.
 */
export const useCountUp = (
  target: number | null,
  duration: number = DURATION_MS
): number | null => {
  const [value, setValue] = useState<number | null>(target);

  // Where the next tween starts: the figure as last painted, fractions and
  // all, kept in a ref because every frame writes it and none of them should
  // be a render on its own account.
  const fromRef = useRef<number | null>(target);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const from = fromRef.current;

    // Nothing to travel between, nowhere to travel to, or nobody who wants to
    // watch it happen.
    if (
      target === null ||
      from === null ||
      from === target ||
      skipAnimation()
    ) {
      fromRef.current = target;
      setValue(target);
      return;
    }

    const startedAt = performance.now();

    // The clock is read rather than taken from the frame's own timestamp: the
    // two are the same clock in a browser, but not everywhere that stands in
    // for one, and a start time on one clock measured against a frame time on
    // another finishes the tween on its first frame.
    const step = () => {
      const progress = Math.min((performance.now() - startedAt) / duration, 1);
      const next =
        progress < 1 ? from + (target - from) * easeOutCubic(progress) : target;

      fromRef.current = next;
      setValue(next);

      frameRef.current = progress < 1 ? requestAnimationFrame(step) : null;
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [target, duration]);

  // `target` rather than the state on the render the target changed on: state
  // is a frame behind by definition, and on the first render with a figure in
  // hand there is no state at all. Falling through to the target means the
  // first paint is the real price, and every paint after it is the tween.
  const display = value ?? target;

  return display === null ? null : Math.round(display);
};

export default useCountUp;
